import React, { useContext } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';

import { useTranslation } from 'react-i18next';
import { AppProvider, AppContext } from './context/AppContext';


import SalesOverview from './pages/SalesOverview';
import POS from './pages/POS';
import CheckPrice from './pages/CheckPrice';
import DailyCustomerPurchases from './pages/DailyCustomerPurchases';
import InventoryAnalytics from './pages/InventoryAnalytics';
import Sales from './pages/Sales';
import PurchaseRegister from './pages/PurchaseRegister';
import Employees from './pages/Employees';
import Departments from './pages/Departments';
import Attendance from './pages/Attendance';
import Payroll from './pages/Payroll';
import ChartOfAccounts from './pages/ChartOfAccounts';
import JournalEntry from './pages/JournalEntry';
import AccountingDashboard from './pages/AccountingDashboard';

import Reports from './pages/Reports';
import Login from './pages/Login';
import Products from './pages/Products';
import Transfers from './pages/Transfers';
import AuditTrail from './pages/AuditTrail';
import CustomerCreditAccounts from './pages/CustomerCreditAccounts';
import SalesTransactions from './pages/SalesTransactions';
import InvoiceArchive from './pages/InvoiceArchive';
import ReturnManagement from './pages/ReturnManagement';
import DailySalesClosing from './pages/DailySalesClosing';
import ExpiryAlerts from './pages/ExpiryAlerts';
import AIExcel from './pages/AIExcel';
import ControlInventory from './pages/ControlInventory';
import UserManagement from './pages/UserManagement';
import PurchaseIntake from './pages/PurchaseIntake';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import FinancialSummary from './pages/FinancialSummary';
import SettingsDashboard from './pages/SettingsDashboard';
import ProductSalesAnalytics from './pages/ProductSalesAnalytics';
import BusinessIntelligenceDashboard from './pages/BusinessIntelligenceDashboard';
import PurchasesModule from './pages/PurchasesModule';
import PurchasesList from './pages/PurchasesList';
import MultiBranchAnalysis from './pages/MultiBranchAnalysis';

import EODLedgerPage from './pages/EODLedgerPage';
import MonthlyMatrixPage from './pages/MonthlyMatrixPage';
import MultiBranchDashboard from './pages/MultiBranchDashboard';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import OwnerSettlement from './pages/OwnerSettlement';
import UnifiedReports from './pages/UnifiedReports';
// ─── 2026 Update Imports ──────────────────────────────────────────────────
import LowStockDashboard from './pages/LowStockDashboard';
import PurchaseOrders from './pages/PurchaseOrders';
import GiftCards from './pages/GiftCards';
import ProductsTracking from './pages/ProductsTracking';

const PrivateRoute = ({ children, requireAdmin }) => {
  const { user, token } = useContext(AppContext);
  
  if (!token || !user) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && user.role !== 'Admin') {
    return <Navigate to="/" replace />;
  }

  return children;
};


const AppLayout = () => {
  const { token } = useContext(AppContext);
  const { t, i18n } = useTranslation();

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    document.documentElement.dir = lng === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = lng;
  };

  // Sync RTL on mount and language change
  React.useEffect(() => {
    document.documentElement.dir = i18n.language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // Sidebar toggle for mobile
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  // Hide sidebar & chrome when on POS
  const location = useLocation();
  const isPOS = location.pathname === '/pos';

  return (
    <div className="flex bg-erp-bg h-screen print:h-auto font-sans overflow-hidden print:overflow-visible">
      {/* Mobile sidebar toggle button — hidden in POS mode */}
      {token && !isPOS && (
        <button
          className="md:hidden fixed top-4 left-4 z-50 bg-erp-accent text-white p-2 rounded-lg shadow-md focus:outline-none"
          onClick={() => setSidebarOpen(true)}
          aria-label="Open sidebar"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M3.75 17.25h16.5" />
          </svg>
        </button>
      )}
      {/* Sidebar — hidden in POS mode */}
      {token && !isPOS && (
        <div className={`no-print fixed inset-0 z-40 md:static md:inset-auto md:z-auto transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`} style={{ width: '16rem', maxWidth: '100vw' }}>
          <Sidebar onClose={() => setSidebarOpen(false)} />
        </div>
      )}
      {/* Overlay for mobile sidebar */}
      {token && sidebarOpen && !isPOS && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-30 md:hidden" onClick={() => setSidebarOpen(false)}></div>
      )}
      {/* Main content — no padding in POS mode */}
      <div className={`flex-1 overflow-y-auto overflow-x-hidden print:overflow-visible print:h-auto ${isPOS ? 'p-0' : ''}`}>
        <div className={isPOS ? '' : 'p-2 md:p-8 print:p-0'}>
          <Routes>
            <Route path="/login" element={
              token ? <Navigate to="/" replace /> : <Login />
            } />
            {/* Protected Routes */}
            <Route path="/" element={<PrivateRoute><SalesOverview /></PrivateRoute>} />
            <Route path="/inventory" element={<PrivateRoute><InventoryAnalytics /></PrivateRoute>} />
            <Route path="/products" element={<PrivateRoute><Products /></PrivateRoute>} />
            <Route path="/products/intake" element={<PrivateRoute><PurchaseIntake /></PrivateRoute>} />
            <Route path="/transfers" element={<PrivateRoute><Transfers /></PrivateRoute>} />
            <Route path="/sales" element={<PrivateRoute><Sales /></PrivateRoute>} />
            <Route path="/purchases" element={<PrivateRoute><PurchaseRegister /></PrivateRoute>} />
            <Route path="/employees" element={<PrivateRoute><Employees /></PrivateRoute>} />
            <Route path="/departments" element={<PrivateRoute><Departments /></PrivateRoute>} />
            <Route path="/attendance" element={<PrivateRoute><Attendance /></PrivateRoute>} />
            <Route path="/payroll" element={<PrivateRoute><Payroll /></PrivateRoute>} />
            <Route path="/accounting" element={<PrivateRoute><AccountingDashboard /></PrivateRoute>} />
            <Route path="/owner-settlement" element={<PrivateRoute requireAdmin={true}><OwnerSettlement /></PrivateRoute>} />
            <Route path="/accounts" element={<PrivateRoute><ChartOfAccounts /></PrivateRoute>} />
            <Route path="/journal" element={<PrivateRoute><JournalEntry /></PrivateRoute>} />

            <Route path="/reports" element={<PrivateRoute><Reports /></PrivateRoute>} />
            <Route path="/customers" element={<PrivateRoute><CustomerCreditAccounts /></PrivateRoute>} />
            <Route path="/purchases/new" element={<PrivateRoute><PurchasesModule /></PrivateRoute>} />
            <Route path="/purchases/edit/:id" element={<PrivateRoute><PurchasesModule /></PrivateRoute>} />
            <Route path="/purchases/today" element={<PrivateRoute><PurchasesList /></PrivateRoute>} />
            <Route path="/invoices" element={<PrivateRoute><SalesTransactions /></PrivateRoute>} />
            <Route path="/archive" element={<PrivateRoute><InvoiceArchive /></PrivateRoute>} />
            <Route path="/returns" element={<PrivateRoute><ReturnManagement /></PrivateRoute>} />
            <Route path="/pos" element={<PrivateRoute><POS /></PrivateRoute>} />
            <Route path="/check-price" element={<PrivateRoute><CheckPrice /></PrivateRoute>} />
            <Route path="/daily-customers" element={<PrivateRoute><DailyCustomerPurchases /></PrivateRoute>} />
            <Route path="/reports/daily" element={<PrivateRoute><DailySalesClosing /></PrivateRoute>} />
            {/* ─── Consolidated Reporting Hub (Max 3 Tabs) ──────────────── */}
            <Route path="/reports" element={<PrivateRoute><UnifiedReports /></PrivateRoute>} />
            <Route path="/reports/performance" element={<PrivateRoute><UnifiedReports /></PrivateRoute>} />
            <Route path="/reports/closing-matrix" element={<PrivateRoute><UnifiedReports /></PrivateRoute>} />
            <Route path="/reports/audit-alerts" element={<PrivateRoute><UnifiedReports /></PrivateRoute>} />

            {/* Reporting aliases redirecting/mounting to the Unified Reporting Hub */}
            <Route path="/executive-dashboard" element={<PrivateRoute requireAdmin={true}><UnifiedReports /></PrivateRoute>} />
            <Route path="/analytics" element={<PrivateRoute requireAdmin={true}><UnifiedReports /></PrivateRoute>} />
            <Route path="/business-analysis" element={<PrivateRoute requireAdmin={true}><UnifiedReports /></PrivateRoute>} />
            <Route path="/reports/eod-ledger" element={<PrivateRoute requireAdmin={true}><UnifiedReports /></PrivateRoute>} />
            <Route path="/reports/monthly-matrix" element={<PrivateRoute requireAdmin={true}><UnifiedReports /></PrivateRoute>} />
            <Route path="/reports/multi-branch-dashboard" element={<PrivateRoute requireAdmin={true}><UnifiedReports /></PrivateRoute>} />
            <Route path="/reports/multi-branch" element={<PrivateRoute requireAdmin={true}><UnifiedReports /></PrivateRoute>} />
            <Route path="/reports/financial" element={<PrivateRoute requireAdmin={true}><UnifiedReports /></PrivateRoute>} />
            <Route path="/reports/expiry" element={<PrivateRoute><UnifiedReports /></PrivateRoute>} />
            <Route path="/sales-reports" element={<PrivateRoute requireAdmin={true}><ProductSalesAnalytics /></PrivateRoute>} />

            {/* System, Tools & Admin */}
            <Route path="/ai-excel" element={<PrivateRoute><AIExcel /></PrivateRoute>} />
            <Route path="/control-inventory" element={<PrivateRoute><ControlInventory /></PrivateRoute>} />
            <Route path="/audit" element={<PrivateRoute requireAdmin={true}><AuditTrail /></PrivateRoute>} />
            <Route path="/users" element={<PrivateRoute requireAdmin={true}><UserManagement /></PrivateRoute>} />
            <Route path="/settings" element={<PrivateRoute><SettingsDashboard /></PrivateRoute>} />
            {/* ─── 2026 Update Routes ─────────────────────────────────────── */}
            <Route path="/low-stock" element={<PrivateRoute><LowStockDashboard /></PrivateRoute>} />
            <Route path="/purchase-orders" element={<PrivateRoute><PurchaseOrders /></PrivateRoute>} />
            <Route path="/gift-cards" element={<PrivateRoute><GiftCards /></PrivateRoute>} />
            <Route path="/products-tracking" element={<PrivateRoute><ProductsTracking /></PrivateRoute>} />
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </AppProvider>
  );
}

export default App;
